import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import SetupPage from './pages/SetupPage';
import ClientGalleryPage from './pages/ClientGalleryPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PreOrderPage from './pages/PreOrderPage';
import PreOrderSuccessPage from './pages/PreOrderSuccessPage';
import PreOrderReviewPage from './pages/PreOrderReviewPage';
import PreOrderEtransferPage from './pages/PreOrderEtransferPage';
import PreOrderCashPage from './pages/PreOrderCashPage';
import OrderCashPage from './pages/OrderCashPage';
import ClientAuthPage from './pages/ClientAuthPage';
import ClientOrdersPage from './pages/ClientOrdersPage';
import ClientSetupPasswordPage from './pages/ClientSetupPasswordPage';
import ClientForgotPasswordPage from './pages/ClientForgotPasswordPage';
import CompleteOrderPage from './pages/CompleteOrderPage';
import DigitalDownloadsPage from './pages/DigitalDownloadsPage';

// Admin Pages
import AdminLayout from './layouts/AdminLayout';
import DashboardPage from './pages/admin/DashboardPage';
import GalleriesPage from './pages/admin/GalleriesPage';
import NewGalleryPage from './pages/admin/NewGalleryPage';
import GalleryDetailPage from './pages/admin/GalleryDetailPage';
import BulkImportPage from './pages/admin/BulkImportPage';
import ProductsPage from './pages/admin/ProductsPage';
import PackagesPage from './pages/admin/PackagesPage';
import TemplatesPage from './pages/admin/TemplatesPage';
import BackgroundsPage from './pages/admin/BackgroundsPage';
import OrdersPage from './pages/admin/OrdersPage';
import BulkEmailPage from './pages/admin/BulkEmailPage';
import DiscountCodesPage from './pages/admin/DiscountCodesPage';
import PricingSheetsPage from './pages/admin/PricingSheetsPage';
import PreOrderEventsPage from './pages/admin/PreOrderEventsPage';
import PreOrdersPage from './pages/admin/PreOrdersPage';
import ReportsPage from './pages/admin/ReportsPage';
import SMSPage from './pages/admin/SMSPage';
import SettingsPage from './pages/admin/SettingsPage';
import EventPhotoUploadPage from './pages/admin/EventPhotoUploadPage';
import EmailResponsesPage from './pages/admin/EmailResponsesPage';

// PWA Pages
import PWALayout from './pages/pwa/PWALayout';
import PWAHome from './pages/pwa/PWAHome';
import PWAGalleries from './pages/pwa/PWAGalleries';
import PWAGalleryView from './pages/pwa/PWAGalleryView';
import PWAOrders from './pages/pwa/PWAOrders';
import PWAAccount from './pages/pwa/PWAAccount';

import './index.css';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/setup" element={<SetupPage />} />
      
      {/* Client gallery (public) */}
      <Route path="/gallery/:accessCode" element={<ClientGalleryPage />} />
      
      {/* Pre-order routes (public) */}
      <Route path="/preorder/:slug" element={<PreOrderPage />} />
      <Route path="/preorder/success/:preorderId" element={<PreOrderSuccessPage />} />
      <Route path="/preorder/cancel/:preorderId" element={<PreOrderPage />} />
      <Route path="/preorder/review/:reviewToken" element={<PreOrderReviewPage />} />
      <Route path="/preorder/etransfer-pending/:preorderId" element={<PreOrderEtransferPage />} />
      <Route path="/preorder/cash-pending/:preorderId" element={<PreOrderCashPage />} />
      <Route path="/order/cash-pending/:orderId" element={<OrderCashPage />} />
      <Route path="/client/auth" element={<ClientAuthPage />} />
      <Route path="/client/orders" element={<ClientOrdersPage />} />
      <Route path="/client/setup-password" element={<ClientSetupPasswordPage />} />
      <Route path="/client/forgot-password" element={<ClientForgotPasswordPage />} />
      <Route path="/client/complete-order/:token" element={<CompleteOrderPage />} />
      <Route path="/downloads/:token" element={<DigitalDownloadsPage />} />
      
      {/* Payment */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      
      {/* Admin routes (protected) */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardPage />} />
        <Route path="galleries" element={<GalleriesPage />} />
        <Route path="galleries/new" element={<NewGalleryPage />} />
        <Route path="galleries/bulk" element={<BulkImportPage />} />
        <Route path="galleries/:id" element={<GalleryDetailPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="packages" element={<PackagesPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="pricing-sheets" element={<PricingSheetsPage />} />
        <Route path="backgrounds" element={<BackgroundsPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="discounts" element={<DiscountCodesPage />} />
        <Route path="bulk-email" element={<BulkEmailPage />} />
        <Route path="responses" element={<EmailResponsesPage />} />
        <Route path="preorder-events" element={<PreOrderEventsPage />} />
        <Route path="preorder-events/:eventId/upload-photos" element={<EventPhotoUploadPage />} />
        <Route path="preorders" element={<PreOrdersPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="sms" element={<SMSPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* PWA Routes for Mobile Clients */}
      <Route path="/app" element={<PWALayout />}>
        <Route index element={<PWAHome />} />
        <Route path="galleries" element={<PWAGalleries />} />
        <Route path="gallery/:accessCode" element={<PWAGalleryView />} />
        <Route path="orders" element={<PWAOrders />} />
        <Route path="account" element={<PWAAccount />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
